import { useEffect, useState } from 'preact/hooks';
import { api } from '@/lib/teaching/teacher-api';
import { TeachingPage, Card } from '@/components/teaching/ui';
import { ErrorLine } from '@/components/teaching/form';
import { KIND_LABEL, type Kind, type Pick } from '@/components/teaching/Picker';

// "All teachers" (2026-10-27): Ali reads every teacher's agent briefs and
// workspace notes in one place. Read-only; admins come from TEACHER_ADMIN_EMAILS.

type Brief = { answers: Record<string, string>; updated_at: string | null };
type Note = { id: string; kind: Kind; text: string; created_at: string; done_at: string | null; picks: Pick[] };
type Onboarding = { answered: number; total: number; fields: Record<string, { source: 'form' | 'chat' | 'tap'; at: string }> };
type Row = { email: string; name: string; briefs: Record<string, Brief>; notes: Note[]; onboarding?: Onboarding };
const SOURCE: Record<string, string> = { form: 'form', chat: 'chat reply', tap: 'kept in chat' };

const AGENT_NAME: Record<string, string> = { main: 'Manager', teaching: 'Teaching', research: 'Research', personal: 'Personal' };
const QUESTIONS: [string, string][] = [
  ['handle', 'What should it handle?'], ['never', 'What should it never do?'], ['when', 'When should it message first?'],
  ['tone', 'How should it talk?'], ['examples', 'Messages he would send it'], ['last_week', 'A real task from last week'],
];
const when = (iso: string) => new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

export function AdminBuildLoop() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api<Row[]>('GET', '/api/admin/build-loop').then(setRows, (e) => setError((e as Error).message)); }, []);

  return (
    <TeachingPage title="All teachers" breadcrumb="What each teacher wrote in My agents and Notes for Ali. Read-only.">
      <ErrorLine error={error} />
      {rows?.length === 0 && <Card><div class="text-[13px] text-[var(--color-text-muted)]">No other teachers yet.</div></Card>}
      {rows?.map((r) => (
        <div key={r.email} class="flex flex-col gap-3">
          <div class="text-[15px] font-semibold">{r.name} <span class="text-[12.5px] font-normal text-[var(--color-text-muted)]">{r.email}</span>
            {r.onboarding && <span data-onboarding-progress class="ml-2 text-[12.5px] font-normal text-[var(--color-text-muted)]">Setup questions: {r.onboarding.answered} of {r.onboarding.total} answered</span>}
          </div>
          <div class="grid gap-3 lg:grid-cols-2">
            {Object.entries(r.briefs).map(([id, b]) => (
              <Card key={id}>
                <div class="flex flex-col gap-2">
                  <div class="flex items-baseline gap-2">
                    <span class="text-[14px] font-semibold">{AGENT_NAME[id] ?? b.answers.name ?? 'New agent'}</span>
                    <span class="text-[12px] text-[var(--color-text-muted)]">{b.updated_at ? `saved ${when(b.updated_at)}` : 'nothing yet'}</span>
                  </div>
                  {QUESTIONS.filter(([q]) => b.answers[q]).map(([q, label]) => (
                    <div key={q} class="flex flex-col gap-0.5">
                      <span class="text-[12px] font-medium text-[var(--color-text-muted)]">{label}
                        {r.onboarding?.fields[`brief:${id}:${q}`] && <span class="font-normal"> · {SOURCE[r.onboarding.fields[`brief:${id}:${q}`].source]}, {when(r.onboarding.fields[`brief:${id}:${q}`].at)}</span>}
                      </span>
                      <span class="text-[13px] whitespace-pre-wrap">{b.answers[q]}</span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
          <Card>
            <div class="flex flex-col gap-3">
              <span class="text-[14px] font-semibold">Notes ({r.notes.length})</span>
              {r.notes.length === 0 && <span class="text-[13px] text-[var(--color-text-muted)]">No notes yet.</span>}
              {r.notes.map((n) => (
                <div key={n.id} class={`flex flex-col gap-2 ${n.done_at ? 'opacity-60' : ''}`}>
                  <span class="text-[12px] text-[var(--color-text-muted)]">{KIND_LABEL[n.kind]} · {when(n.created_at)}{n.done_at ? ' · done' : ''}</span>
                  {n.text && <span class="text-[13px] whitespace-pre-wrap">{n.text}</span>}
                  {n.picks.length > 0 && (
                    <div class="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                      {n.picks.map((p, i) => (
                        <div key={i} class="rounded-xl bg-[var(--color-elevated)] p-2 flex flex-col gap-1">
                          {p.shot && <img src={p.shot} alt={p.text} class="max-h-32 object-contain" />}
                          <span class="text-[12.5px] line-clamp-2">{p.text || '(no text)'}</span>
                          <span class="text-[11.5px] text-[var(--color-text-muted)] truncate">{p.page}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      ))}
    </TeachingPage>
  );
}
